import { useEffect, useMemo, useState } from 'react';
import { fetchFilterDirectory } from '../services/createConfig.api';

export function useCreateConfigOptions({ operator, unit, evn }) {
  const [dir, setDir] = useState({ operators: [], units: [], meters: [], evns: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchFilterDirectory();
        if (mounted) setDir(data);
      } catch (e) {
        if (mounted) setError(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const {
    operatorOptions, unitOptions, evnOptions, meterNoOptions, nameToCode, codeToName, flags,
  } = useMemo(() => {
    const operators = dir.operators ?? [];
    const units = dir.units ?? [];
    const meters = dir.meters ?? [];
    const evns = dir.evns ?? [];

    const norm = (v) => String(v ?? '').trim();

    // Build code/name maps
    const nameToCode = new Map();
    const codeToName = new Map();
    operators.forEach(o => {
      const code = norm(o.OperatorCode);
      const name = norm(o.OperatorName);
      if (code && name) {
        nameToCode.set(name, code);
        codeToName.set(code, name);
      }
    });

    const unitsByOperator = new Map(); 
    const evnsByUnit = new Map();      
    const metersByEvn = new Map();     
    const metersByUnit = new Map();    
    const unitByEvn = new Map();      

    const allUnits = new Set(units.map(u => norm(u.UnitName)).filter(Boolean));

    units.forEach(u => {
      const uName = norm(u.UnitName);
      const opCode = norm(u.OperatorCode);
      if (!unitsByOperator.has(opCode)) unitsByOperator.set(opCode, new Set());
      if (uName) unitsByOperator.get(opCode).add(uName);
    });

    const evnSource = (evns.length ? evns : meters)
      .map(e => ({ EVN: norm(e.EVN), UnitName: norm(e.UnitName) }))
      .filter(e => e.EVN);

    evnSource.forEach(e => {
      const uName = e.UnitName;
      const eVal = e.EVN;
      if (!evnsByUnit.has(uName)) evnsByUnit.set(uName, new Set());
      if (uName && eVal) {
        evnsByUnit.get(uName).add(eVal);
        unitByEvn.set(eVal, uName);
      }
    });

    meters.forEach(m => {
      const eVal = norm(m.EVN);
      const uName = norm(m.UnitName);
      const mNo = norm(m.MeterNumber);
      if (mNo) {
        if (eVal) {
          if (!metersByEvn.has(eVal)) metersByEvn.set(eVal, new Set());
          metersByEvn.get(eVal).add(mNo);
        }
        if (uName) {
          if (!metersByUnit.has(uName)) metersByUnit.set(uName, new Set());
          metersByUnit.get(uName).add(mNo);
        }
      }
    });

    const hasOperator = !!operator;
    const selectedOpCode = hasOperator ? operator : null;

    const unitIsInAll = unit ? allUnits.has(norm(unit)) : false;
    const unitBelongsToOperator = unitIsInAll && selectedOpCode
      ? (unitsByOperator.get(selectedOpCode) ?? new Set()).has(norm(unit))
      : false;
    const isExistingUnit = hasOperator && unit && unitBelongsToOperator;

    const evnsForUnit = isExistingUnit ? (evnsByUnit.get(norm(unit)) ?? new Set()) : new Set();
    const isExistingEvn = isExistingUnit && evn ? evnsForUnit.has(norm(evn)) : false;

    const operatorOptions = [...codeToName.keys()].sort();

    let unitOptions = [];
    if (hasOperator) {
      unitOptions = [...(unitsByOperator.get(selectedOpCode) ?? new Set())].sort();
    }

    let evnOptions = [];
    if (isExistingUnit) {
      evnOptions = [...evnsForUnit].sort();
    }

    let meterNoOptions = [];
    if (isExistingEvn) {
      meterNoOptions = [...(metersByEvn.get(norm(evn)) ?? new Set())].sort();
    }

    const flags = { hasOperator, isExistingUnit, isExistingEvn };

    return {
      operatorOptions,
      unitOptions,
      evnOptions,
      meterNoOptions,
      nameToCode,
      codeToName,
      flags,
    };
  }, [dir, operator, unit, evn]);

  return {
    operatorOptions,
    unitOptions,
    evnOptions,
    meterNoOptions,
    loading,
    error,
    nameToCode,
    codeToName,
    flags,
  };
}
